/**
 * The Photoreal render window's preview: its settings shown, roughly, in the
 * live 3D view — so a slider's effect can be judged without a render.
 *
 * The browser draws with its own look (renderSettings.ts) and Blender with
 * its own, so the photoreal values cannot simply be copied across. Instead
 * each photoreal setting NUDGES the view's matching setting by how far it
 * has moved from its factory value: with every photoreal setting at factory
 * the preview is exactly the view as it is, and each change shows its
 * direction and rough size. What the browser cannot draw at all is listed in
 * NOT_PREVIEWED and said in the window.
 */

import { RenderSettings } from './renderSettings';
import { ContrastLook, FACTORY_PHOTO_RENDER, PhotoRenderSettings } from './photoRender';

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** AgX's contrast looks as a multiplier on the view's contrast (Base = as is). */
const CONTRAST: Record<ContrastLook, number> = {
  'Very Low': 0.8, Low: 0.88, 'Medium Low': 0.94, Base: 1, 'Medium High': 1.07, High: 1.15, 'Very High': 1.25, Punchy: 1.1,
};

/** The photoreal settings the 3D view cannot show (the window lists them when the preview is on). */
export const NOT_PREVIEWED = ['specular', 'clear coat', 'depth of field and bokeh', 'colour fringing', 'the HDRI picture itself', 'the view outside', 'the yard (lawn, driveway, fence, trees)', 'photographic textures', 'a fisheye lens', 'the 360', 'samples and output size'] as const;

/** The view's look with the photoreal settings' changes laid over it. */
export function photoPreviewSettings(view: RenderSettings, photo: PhotoRenderSettings): RenderSettings {
  const F = FACTORY_PHOTO_RENDER;
  const out: RenderSettings = { ...view };

  // Finishes: the boards' roughness (the browser draws Wall&CeilingBoard and
  // SlatWall with one board material), the floor's.
  const boardShift = (photo.materials.wcb.roughness - F.materials.wcb.roughness + photo.materials.slatwall.roughness - F.materials.slatwall.roughness) / 2;
  out.boardRoughness = clamp(view.boardRoughness + boardShift, 0, 1);
  out.floorRoughness = clamp(view.floorRoughness + (photo.materials.floor.roughness - F.materials.floor.roughness), 0, 1);
  // The joints: their visibility IS the view's seam strength (0 hides them in both).
  if (photo.materials.wcb.joints !== null) out.seamStrength = clamp(photo.materials.wcb.joints, 0, 1);

  // Light: the sun as the view's key light, the sky or HDRI as its environment, the lamps.
  if (photo.light.sun === 'off' || (photo.light.sun === 'auto' && photo.light.world === 'hdri')) {
    out.keyIntensity = 0;
  } else {
    out.keyIntensity = clamp(view.keyIntensity * (photo.light.sunStrength / F.light.sunStrength), 0, 1);
    out.keyElevation = clamp(view.keyElevation + (photo.light.sunElevation - F.light.sunElevation), -10, 85);
  }
  const outside = photo.light.world === 'hdri' ? photo.light.hdriStrength : photo.light.skyStrength / F.light.skyStrength;
  out.envIntensity = clamp(view.envIntensity * outside, 0, 2);
  out.interiorIntensity = clamp(view.interiorIntensity * photo.light.lampScale, 0, 1.5);

  // Effects.
  out.exposure = clamp(view.exposure * Math.pow(2, photo.effects.exposure), 0.3, 2.5);
  if (photo.effects.view === 'Filmic') out.toneMapping = 'aces';
  else if (photo.effects.view === 'Standard') out.toneMapping = 'linear';
  out.contrast = clamp(view.contrast * (photo.effects.view === 'AgX' ? CONTRAST[photo.effects.contrast] : 1), 0.7, 1.5);
  out.saturation = clamp(view.saturation * photo.effects.saturation * (photo.effects.view === 'AgX' && photo.effects.contrast === 'Punchy' ? 1.1 : 1), 0, 2);
  if (photo.effects.contact <= 0) out.ao = false;
  else out.aoIntensity = clamp(view.aoIntensity * photo.effects.contact, 0, 2);
  if (photo.effects.bloom > 0) {
    out.bloom = true;
    out.bloomStrength = clamp((view.bloom ? view.bloomStrength : 0) + photo.effects.bloom, 0, 2);
  }
  out.vignette = clamp(view.vignette + photo.effects.vignette, 0, 1);
  return out;
}
